/**
 * Club nuvvu (plan 2.1 §1 y §7, decisiones de Gabor del 4/11): suscripción de compradores.
 *
 *   settings/club            — parámetros editables sin desplegar (precios, prueba, % de puntos,
 *                              bienvenida, ventajas de protección). Si falta, valen CLUB_DEFAULTS.
 *   clubMemberships/{uid}    — estado de la suscripción (solo Functions escribe; el dueño lee).
 *   users/{uid}.club_member  — bandera para la insignia y las ventajas (campo privilegiado).
 *
 * Puntos: 1 % del precio del artículo (`points_bps` = 100) al completarse el pedido, como saldo de
 * premio (`userPerks` promo_credit, source `club`) que no caduca y no se retira nunca.
 */
import { z } from 'zod';
import { MoneySchema, TimestampSchema, UserIdSchema, type Money } from './primitives.js';

export const CLUB_PLANS = ['monthly', 'annual'] as const;
export const ClubPlanSchema = z.enum(CLUB_PLANS);
export type ClubPlan = z.infer<typeof ClubPlanSchema>;

export const ClubSettingsSchema = z.object({
  /** Cuota mensual (Gabor: 2 €). */
  monthly_price: MoneySchema,
  /** Plan anual (por confirmar; propuesta 19,99 €). */
  annual_price: MoneySchema,
  /** Primer mes gratis (solo la primera vez que alguien se apunta). */
  trial_days: z.number().int().min(0).max(90),
  /** Puntos en centésimas de punto porcentual: 100 = 1 % del artículo. */
  points_bps: z.number().int().min(0).max(1000),
  /** Bienvenida en saldo de puntos (Gabor: 1 €); se convierte a la moneda del monedero. */
  welcome_credit: MoneySchema,
  /** Gabor 4/11: el socio no paga la parte fija de la protección (0,70 €), solo el 5 %. */
  waive_fixed_protection: z.boolean(),
  /** Gabor 4/11: la protección sin parte fija vale para las primeras N compras de cada mes (6). */
  fixed_waiver_monthly_cap: z.number().int().min(0).max(100),
  /** Gabor 4/11: horas de más para reclamar (protección ampliada): 72. */
  extra_claim_hours: z.number().int().min(0).max(720),
  /** Gabor 4/11: las ofertas para socios empiezan estos días antes que para el resto. */
  early_offer_days: z.number().int().min(0).max(14),
  /** Gabor 4/11: los avisos (búsquedas, favoritos) llegan al resto estas horas después que al socio. */
  alert_lead_hours: z.number().int().min(0).max(48),
  /** Garantía de ahorro del primer año (§7.5). */
  savings_guarantee: z.boolean(),
  updated_at: TimestampSchema.optional(),
  updated_by: UserIdSchema.optional(),
});
export type ClubSettings = z.infer<typeof ClubSettingsSchema>;

export const CLUB_DEFAULTS: Omit<ClubSettings, 'updated_at' | 'updated_by'> = {
  // Gabor 4/11 15:18: 2,99 €/mes y 29,99 €/año (antes 2 € y 19,99 €).
  monthly_price: { amount: 299, currency: 'EUR' },
  annual_price: { amount: 2999, currency: 'EUR' },
  trial_days: 30,
  points_bps: 100,
  welcome_credit: { amount: 100, currency: 'EUR' },
  waive_fixed_protection: true,
  fixed_waiver_monthly_cap: 6,
  extra_claim_hours: 72,
  early_offer_days: 2,
  alert_lead_hours: 2,
  savings_guarantee: true,
};

/** Lo guardado en settings/club encima de los valores por defecto (campo a campo). */
export function clubSettingsOf(raw: unknown): Omit<ClubSettings, 'updated_at' | 'updated_by'> {
  // Campo a campo: un valor mal guardado no tira los demás (vuelve solo ese al de por defecto).
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const shape = ClubSettingsSchema.shape;
  const p: Partial<ClubSettings> = {};
  for (const k of Object.keys(CLUB_DEFAULTS) as (keyof typeof CLUB_DEFAULTS)[]) {
    const r = shape[k].safeParse(src[k]);
    if (r.success) (p as Record<string, unknown>)[k] = r.data;
  }
  return {
    monthly_price: p.monthly_price ?? CLUB_DEFAULTS.monthly_price,
    annual_price: p.annual_price ?? CLUB_DEFAULTS.annual_price,
    trial_days: p.trial_days ?? CLUB_DEFAULTS.trial_days,
    points_bps: p.points_bps ?? CLUB_DEFAULTS.points_bps,
    welcome_credit: p.welcome_credit ?? CLUB_DEFAULTS.welcome_credit,
    waive_fixed_protection: p.waive_fixed_protection ?? CLUB_DEFAULTS.waive_fixed_protection,
    fixed_waiver_monthly_cap: p.fixed_waiver_monthly_cap ?? CLUB_DEFAULTS.fixed_waiver_monthly_cap,
    extra_claim_hours: p.extra_claim_hours ?? CLUB_DEFAULTS.extra_claim_hours,
    early_offer_days: p.early_offer_days ?? CLUB_DEFAULTS.early_offer_days,
    alert_lead_hours: p.alert_lead_hours ?? CLUB_DEFAULTS.alert_lead_hours,
    savings_guarantee: p.savings_guarantee ?? CLUB_DEFAULTS.savings_guarantee,
  };
}

/** Mes (UTC, `AAAA-MM`) para el tope mensual de compras sin parte fija. */
export const clubMonthKey = (ms: number = Date.now()): string =>
  new Date(ms).toISOString().slice(0, 7);

/** ¿Le quedan compras sin parte fija este mes? (`used` = las ya hechas este mes). */
export const clubFixedWaiverLeft = (used: number, cap: number): boolean => used < cap;

/** Puntos de un pedido: `bps` sobre el precio del artículo, redondeado hacia abajo (céntimos). */
export function clubPointsFor(item: Money, bps: number): Money {
  const amount = Math.floor((Math.max(0, item.amount) * Math.max(0, bps)) / 10_000);
  return { amount, currency: item.currency };
}

export const CLUB_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'cancelled',
  'expired',
  'paused',
] as const;
export const ClubStatusSchema = z.enum(CLUB_STATUSES);
export type ClubStatus = z.infer<typeof ClubStatusSchema>;

/** Con estos estados el socio tiene las ventajas (past_due: Stripe aún reintenta el cobro). */
export const clubLive = (s: string | null | undefined): boolean =>
  s === 'active' || s === 'trialing' || s === 'past_due';

export const ClubMembershipSchema = z.object({
  uid: UserIdSchema,
  source: z.enum(['stripe', 'app_store', 'play_store']),
  plan: ClubPlanSchema,
  status: ClubStatusSchema,
  renews_at: TimestampSchema.nullable(),
  cancel_at: TimestampSchema.nullable(),
  trial_end: TimestampSchema.nullable(),
  external_id: z.string().nullable(),
  /** Primera alta: decide si hay mes gratis y la bienvenida (una sola vez). */
  first_joined_at: TimestampSchema,
  welcome_granted: z.boolean(),
  updated_at: TimestampSchema,
});
export type ClubMembership = z.infer<typeof ClubMembershipSchema>;
